import {
  ApplyMomentumArguments,
  ApplyMomentumReturn,
  CreatureState,
  CreatureStateType,
  CreatureType,
  HostilityType,
  MMONetSchema,
  PlayerState,
  TimedMomentumGameObject,
} from "mmo-common";
import {
  GameObject,
  PhysicsTranslationForce,
  ProcessGameObject,
  Quat,
  TimerGameObject,
  Vector3,
  quatToVector2D,
  vector2AngleBetween,
  vector2DToQuat,
  vector2Rotate,
  vector3Add,
  vector3Subtract,
} from "realms-engine";
import { PhysicsPredictionGameObject } from "realms-engine-network-client";
import { BaseCharacterGameObject } from "../base-character/baseCharacter.gameObject";
import { MMONetworkSlave } from "@utils/types";
import { NetworkTypeEvent } from "realms-engine-network";
import THREE from "realms-three";
import {
  BASE_EMISSIVE_COLOR,
  HIT_FLASH_DURATION_MS,
  SELF_HIT_FLASH_COLOR,
} from "../config/combat";
import { MMOCreature } from "@game/models/mmoCreature";

// The angle turned per second
const TURN_ANGLE_SPEED = 6 * Math.PI;

export interface ControllablePlayerGameObjectEventMap {
  isTouchingGround: boolean;
  inCombatChange: { inCombat: boolean };
  onMasterCreatureStateChange: { newState: CreatureState };
}

/**
 * The game object that controls the current player
 */
export class ControllablePlayerGameObject
  extends GameObject<ControllablePlayerGameObjectEventMap>
  implements MMONetworkSlave<CreatureType.Player>, MMOCreature
{
  readonly movingForce = new PhysicsTranslationForce({ x: 0, y: 0, z: 0 });
  readonly characterGameObject: BaseCharacterGameObject;
  private physicsPredictionGameObject: PhysicsPredictionGameObject | null =
    null;
  // The master state on the server
  private masterPlayerState: PlayerState;

  // The quaternion of the character threejs object
  // This is changed smoothly
  private characterQuaternion: Quat;

  private onHitFlashEndTimer: TimerGameObject;

  constructor(state: PlayerState) {
    super();
    this.characterGameObject = this.addChild(
      new BaseCharacterGameObject({ character: state.character })
    );
    this.masterPlayerState = state;
    this.addChild(new ProcessGameObject({}, (delta) => this.onStep(delta)));
    this.characterQuaternion = state.creature.quaternion;
    this.onHitFlashEndTimer = this.addChild(new TimerGameObject());

    this.onHitFlashEndTimer.addListener("onFinish", () => {
      this.characterGameObject.baseCharacterMesh.setEmissive(
        new THREE.Color(BASE_EMISSIVE_COLOR)
      );
    });
  }

  isDead(): boolean {
    return (
      this.getMasterPlayerState().creature.state.type ===
      CreatureStateType.Death
    );
  }

  getHostility(): HostilityType {
    return this.getMasterPlayerState().creature.hostility;
  }

  onNetworkEvent(
    event: NetworkTypeEvent<MMONetSchema, CreatureType.Player, "onHurt">
  ): void {
    switch (event.name) {
      case "onHurt": {
        this.characterGameObject.baseCharacterMesh.setEmissive(
          SELF_HIT_FLASH_COLOR
        );
        this.onHitFlashEndTimer.start(HIT_FLASH_DURATION_MS);
        break;
      }
    }
  }

  onStep(delta: number) {
    // Move character quaternion towards current quaternion
    const characterQuatVector = quatToVector2D(this.characterQuaternion);
    const targetQuatVector = quatToVector2D(this.getQuaternion());
    const angleDiff = vector2AngleBetween(
      targetQuatVector,
      characterQuatVector
    );
    const maxAngle = (TURN_ANGLE_SPEED * delta) / 1000;
    const clampedAngle = Math.max(Math.min(maxAngle, angleDiff), -1 * maxAngle);
    const newCharacterQuatVector = vector2Rotate(
      characterQuatVector,
      clampedAngle
    );
    this.characterQuaternion = vector2DToQuat(newCharacterQuatVector);

    this.characterGameObject.setQuaternion(this.characterQuaternion);
    this.characterGameObject.setPosition(this.getPosition());
  }

  onSlaveChanges(
    changes: Partial<PlayerState>,
    newState: PlayerState,
    oldState: PlayerState
  ): void {
    this.masterPlayerState = newState;
    if (changes.inCombat !== undefined) {
      this.dispatch("inCombatChange", {
        inCombat: changes.inCombat,
      });
    }
    if (changes.creature?.state !== undefined) {
      this.dispatch("onMasterCreatureStateChange", {
        newState: newState.creature.state,
      });
    }
  }

  getMasterPlayerState() {
    return this.masterPlayerState;
  }

  /**
   * Sets the physics prediction object
   */
  setPhysicsPredictionGameObject(
    physicsPredictionGameObject: PhysicsPredictionGameObject
  ) {
    if (this.physicsPredictionGameObject === physicsPredictionGameObject) {
      return;
    }
    if (this.physicsPredictionGameObject) {
      this.physicsPredictionGameObject.physicsObject.removeListener(
        "isTouchingGround",
        this.onIsTouchingGround
      );
      this.physicsPredictionGameObject.physicsObject.removeTranslationForce(
        this.movingForce
      );
    }
    this.physicsPredictionGameObject = physicsPredictionGameObject;
    this.physicsPredictionGameObject.setEnableClientSidePrediction(true);
    this.physicsPredictionGameObject.physicsObject.addTranslationForce(
      this.movingForce
    );

    // Set initial position to master state
    this.setPosition(this.masterPlayerState.creature.position);
    this.setQuaternion(this.masterPlayerState.creature.quaternion);

    this.physicsPredictionGameObject.physicsObject.addListener(
      "isTouchingGround",
      this.onIsTouchingGround
    );
  }

  /**
   * Called when the is touching ground event is fired
   */
  private onIsTouchingGround = (isTouching: boolean) => {
    this.dispatch("isTouchingGround", isTouching);
  };

  /**
   * Gets quaternion
   */
  getQuaternion(): Quat {
    if (!this.physicsPredictionGameObject) {
      return this.masterPlayerState.creature.quaternion;
    }
    return this.physicsPredictionGameObject.physicsObject.getQuaternion();
  }

  /**
   * Set quaternion
   */
  setQuaternion(quat: Quat) {
    if (!this.physicsPredictionGameObject) {
      return;
    }
    this.physicsPredictionGameObject.physicsObject.setQuaternion(quat);
  }

  /**
   * Gets the position
   */
  getPosition(): Vector3 {
    if (!this.physicsPredictionGameObject) {
      // Get current position
      return this.masterPlayerState.creature.position;
    }
    return vector3Subtract(
      this.physicsPredictionGameObject.physicsObject.getPosition(),
      {
        x: 0,
        // We want to make the position the bottom of the physics body
        y: this.physicsPredictionGameObject.physicsObject.getHeight() * 0.5,
        z: 0,
      }
    );
  }

  /**
   * Sets the position
   */
  setPosition(position: Vector3): void {
    if (!this.physicsPredictionGameObject) {
      return;
    }
    this.physicsPredictionGameObject.physicsObject.setPosition(
      vector3Add(position, {
        x: 0,
        y: this.physicsPredictionGameObject.physicsObject.getHeight() * 0.5,
        z: 0,
      })
    );
  }

  addVelocity(velocity: Vector3) {
    this.physicsPredictionGameObject?.physicsObject.addVelocity(velocity);
  }

  applyMomentum(momentum: ApplyMomentumArguments): ApplyMomentumReturn {
    const timedMomentumGameObject = new TimedMomentumGameObject(momentum);
    const physicsBody = this.physicsPredictionGameObject?.physicsObject;
    if (!physicsBody) {
      return {
        removeMomentum: () => {},
      };
    }
    physicsBody.addChild(timedMomentumGameObject);
    return {
      removeMomentum: () => physicsBody.removeChild(timedMomentumGameObject),
    };
  }

  getHealth() {
    return this.getMasterPlayerState().creature.health;
  }

  getMaxHealth() {
    return this.getMasterPlayerState().creature.maxHealth;
  }

  getPhysicsGameObjectId() {
    return this.getMasterPlayerState().physicsGameObjectId;
  }

  getInRangeInteractableIds() {
    return this.getMasterPlayerState().inRangeInteractableIds;
  }

  /**
   * Whether the player is in combat
   */
  isInCombat() {
    return this.getMasterPlayerState().inCombat;
  }
}
